import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { eur, num, formatApiError, partnerLogoUrl } from "@/lib/api";
import PointsMap from "@/components/PointsMap";
import ConvertPartnerDialog from "@/components/ConvertPartnerDialog";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Search, Upload, Pencil, Trash2, Download, Loader2, Handshake, Database, MapPin, Building2, Skull, Filter, Wrench, ClipboardCheck, LocateFixed, Users } from "lucide-react";
import QuickAdd from "@/components/QuickAdd";
import PresidioDialog from "@/components/PresidioDialog";
import PinPicker from "@/components/PinPicker";
import VisitaB2BDialog from "@/components/VisitaB2BDialog";
import { matrixQuadrantKey } from "@/lib/classification";
import { CAT_STYLE } from "@/components/ProfilazioneB2B";
import ClassLegend from "@/components/ClassLegend";
import VestaInfo from "@/components/VestaInfo";
import VestaBadge from "@/components/VestaBadge";

const EMPTY = {
  name: "", piva: "", referente: "", email: "", phone: "", region: "", province: "", city: "",
  address: "", certification_level: "Base", agency_id: "none", products: [],
  sede_op_address: "", sede_op_city: "", sede_op_cap: "", lat: null, lng: null, geo_manual: false,
  importo_totale: 0,
  ultima_visita: "", ultimo_acquisto: "", status: "active", is_partner: true,
  partner_class: "none", potenziale_annuo: "", acquisti_torri_anno: "",
  ultimo_contatto: "", prossima_azione: "", data_prossima_azione: "", note_commerciali: "",
  profilo_b2b: {},
};
const CERTS = ["Base", "Silver", "Gold", "Platinum"];

export default function Partners({ mode }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const canEditCard = isAdmin || user?.role === "back_office" || user?.role_actual === "back_office" || user?.role === "head_of_partner";
  const isAgency = user?.role === "agency";
  const canEditPartner = canEditCard || isAgency; // l'agente può modificare i propri clienti
  const canConvert = ["admin", "commercial_manager"].includes(user?.role);
  const canQuickAdd = ["admin", "commercial_manager", "agency"].includes(user?.role);
  const canPresidio = ["admin", "commercial_manager", "agency"].includes(user?.role);
  const [partners, setPartners] = useState([]);
  const [presidioView, setPresidioView] = useState("attivo");
  const [visitaOpen, setVisitaOpen] = useState(false);
  const [prospectsLite, setProspectsLite] = useState([]);
  const [managersLite, setManagersLite] = useState([]);
  const insertActor = user?.role === "agency" ? "agenzia" : "responsabile_commerciale";
  const openVisita = async () => {
    try {
      const [pr, mg] = await Promise.all([
        api.get("/prospects"),
        user?.role === "admin" ? api.get("/users?role=commercial_manager") : Promise.resolve({ data: [] }),
      ]);
      setProspectsLite(pr.data || []);
      setManagersLite(mg.data || []);
    } catch { /* noop */ }
    setVisitaOpen(true);
  };
  const [presidioCounts, setPresidioCounts] = useState({ panchina: 0, cimitero: 0 });
  const [presidioDlg, setPresidioDlg] = useState({ open: false, target: null, mode: "move" });
  const [agencies, setAgencies] = useState([]);
  const [regions, setRegions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [pfilter, setPfilter] = useState("all");
  const [profFilter, setProfFilter] = useState("all");
  const [regionFilter, setRegionFilter] = useState("all");
  const [provinceFilter, setProvinceFilter] = useState("all");
  const [agencyFilter, setAgencyFilter] = useState("all");
  const [sortOpp, setSortOpp] = useState(false);
  const [geo, setGeo] = useState([]);
  const [geoStat, setGeoStat] = useState(null);
  const [geoBusy, setGeoBusy] = useState(false);
  const navigate = useNavigate();
  const geoPoll = useRef(null);
  useEffect(() => { api.get("/dashboard/clients-map").then((r) => setGeo(r.data || [])).catch(() => {}); }, []);
  const coordById = useMemo(() => Object.fromEntries(geo.map((c) => [c.id, c])), [geo]);

  const loadGeoStat = async () => {
    try {
      const r = await api.get("/import/geocode-status");
      setGeoStat(r.data);
      if (!r.data.running && geoPoll.current) { clearInterval(geoPoll.current); geoPoll.current = null; }
      return r.data;
    } catch { return null; }
  };
  const startGeocode = async () => {
    setGeoBusy(true);
    try {
      await api.post("/partners/geocode");
      toast.success("Geolocalizzazione avviata in background");
      await loadGeoStat();
      if (geoPoll.current) clearInterval(geoPoll.current);
      geoPoll.current = setInterval(async () => {
        const s = await loadGeoStat();
        if (s && !s.running) { api.get("/dashboard/clients-map").then((r) => setGeo(r.data || [])).catch(() => {}); }
      }, 5000);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Impossibile avviare la geolocalizzazione"); }
    finally { setGeoBusy(false); }
  };
  useEffect(() => { if (isAdmin) loadGeoStat(); return () => { if (geoPoll.current) clearInterval(geoPoll.current); }; }, [isAdmin]);
  const [dialog, setDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [geoOne, setGeoOne] = useState(false);
  const ricalcolaPos = async () => {
    if (!editing) return;
    setGeoOne(true);
    try {
      const { data } = await api.post(`/partners/${editing.id}/ricalcola-posizione`);
      setForm((f) => ({ ...f, lat: data.lat, lng: data.lng, geo_manual: false }));
      toast.success(data.preciso ? "Posizione ricalcolata dall'indirizzo" : "Posizione approssimata (indirizzo poco preciso)");
    } catch { toast.error("Errore ricalcolo posizione"); }
    finally { setGeoOne(false); }
  };
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [csBusy, setCsBusy] = useState(false);
  const [csCandidates, setCsCandidates] = useState(null);
  const fileRef = useRef();
  const [geoConsent, setGeoConsent] = useState(null);
  const [consentOpen, setConsentOpen] = useState(false);
  const [myPos, setMyPos] = useState(null);
  const [locating, setLocating] = useState(false);
  const [showAgents, setShowAgents] = useState(false);
  const [agentPositions, setAgentPositions] = useState([]);
  const geoWatch = useRef(null);
  const agentsPoll = useRef(null);
  const canLocate = ["agency", "commercial_manager"].includes(user?.role);
  const todayIso = new Date().toISOString().slice(0, 10);
  const [trailAgent, setTrailAgent] = useState(null);
  const [trailDay, setTrailDay] = useState(todayIso);
  const [trail, setTrail] = useState([]);

  const enrichCreditsafe = async (connectId) => {
    if (!editing) { toast.error("Salva prima il cliente, poi arricchisci"); return; }
    setCsBusy(true);
    try {
      const body = connectId ? { connect_id: connectId, candidates: csCandidates || [] } : {};
      const r = await api.post(`/creditsafe/enrich/${editing}`, body, { timeout: 60000 });
      if (r.data.status === "multiple") { setCsCandidates(r.data.candidates); toast.message("Più aziende trovate: seleziona quella corretta"); return; }
      const cs = r.data.creditsafe || {};
      setCsCandidates(null);
      setForm((f) => ({ ...f, piva: f.piva || cs.vat_no || "", province: f.province || cs.province || "", city: f.city || cs.city || "", address: f.address || cs.address || "" }));
      toast.success(r.data.report_access ? "Arricchito da Creditsafe (report incluso)" : "Arricchito da Creditsafe (anagrafica). Report IT non abilitato sull'account.");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Errore Creditsafe"); }
    finally { setCsBusy(false); }
  };

  const load = async () => {
    setLoading(true);
    const [p, a, r] = await Promise.all([api.get("/partners", { params: { presidio: presidioView } }), api.get("/agencies").catch(() => ({ data: [] })), api.get("/regions")]);
    setPartners(p.data);
    setAgencies(a.data);
    setRegions(r.data);
    api.get("/partners/presidio-counts").then((r) => setPresidioCounts(r.data)).catch(() => {});
    setLoading(false);
  };

  useEffect(() => { load(); }, [presidioView]);

  const filtered = useMemo(() => {
    const s = q.toLowerCase();
    let arr = partners.filter((p) => {
      if (pfilter === "partner" && !p.is_partner) return false;
      if (pfilter === "cliente" && p.is_partner) return false;
      if (pfilter === "sciolti" && p.agency_id) return false;
      const pr = p.profilazione || {};
      if (["A", "B", "C"].includes(profFilter) && pr.categoria_auto !== profFilter) return false;
      if (profFilter === "opportunita" && pr.stato_commerciale !== "opportunita") return false;
      if (profFilter === "a_rischio" && pr.stato_commerciale !== "a_rischio") return false;
      if (profFilter === "incompleta" && !pr.incompleta) return false;
      if (profFilter.startsWith("quad_")) {
        if (!pr.categoria_auto) return false;
        if (matrixQuadrantKey(pr) !== profFilter.slice(5)) return false;
      }
      if (regionFilter !== "all" && p.region !== regionFilter) return false;
      if (provinceFilter !== "all" && p.province !== provinceFilter) return false;
      if (agencyFilter !== "all" && (agencyFilter === "none" ? !!p.agency_id : p.agency_id !== agencyFilter)) return false;
      return [p.name, p.region, p.city, p.agency_name, p.piva].filter(Boolean).some((v) => String(v).toLowerCase().includes(s));
    });
    if (sortOpp) arr = [...arr].sort((a, b) => ((b.profilazione?.potenziale_non_espresso || 0) - (a.profilazione?.potenziale_non_espresso || 0)));
    return arr;
  }, [partners, q, pfilter, profFilter, regionFilter, provinceFilter, agencyFilter, sortOpp]);

  const provinceOptions = useMemo(() => {
    const s = new Set(partners.filter((p) => regionFilter === "all" || p.region === regionFilter).map((p) => p.province).filter(Boolean));
    return [...s].sort();
  }, [partners, regionFilter]);

  const mapPoints = useMemo(() => filtered.map((p) => {
    const c = coordById[p.id];
    if (!c) return null;
    return { id: p.id, lat: c.lat, lng: c.lng, color: p.is_partner ? "#16a34a" : "#f59e0b", logoUrl: p.has_logo ? partnerLogoUrl(p.id) : null, tooltip: `${p.name} · ${p.is_partner ? "Partner" : "Cliente"}${p.city ? " · " + p.city : ""}` };
  }).filter(Boolean), [filtered, coordById]);

  const inWorkingHours = () => { const d = new Date(); return d.getDay() >= 1 && d.getDay() <= 5 && d.getHours() >= 8 && d.getHours() < 19; };
  const pingPos = (silent = false) => {
    if (!navigator.geolocation) { if (!silent) toast.error("Geolocalizzazione non supportata dal dispositivo"); return; }
    navigator.geolocation.getCurrentPosition(async (p) => {
      const { latitude, longitude, accuracy } = p.coords;
      setMyPos({ lat: latitude, lng: longitude, accuracy });
      try { await api.post("/geo/ping", { lat: latitude, lng: longitude, accuracy }); } catch { /* ignore */ }
    }, (err) => { if (!silent) toast.error("Impossibile ottenere la posizione: " + (err.message || "permesso negato")); },
    { enableHighAccuracy: true, timeout: 15000, maximumAge: 30000 });
  };
  const startWatch = () => { if (!geoWatch.current) geoWatch.current = setInterval(() => { if (inWorkingHours()) pingPos(true); }, 180000); };
  const startLocating = async () => {
    setLocating(true);
    try {
      let c = geoConsent;
      if (!c) { const r = await api.get("/geo/consent"); c = r.data; setGeoConsent(r.data); }
      if (!c?.accepted) { setConsentOpen(true); return; }
      pingPos(false); startWatch();
    } finally { setLocating(false); }
  };
  const acceptConsent = async () => {
    try { await api.post("/geo/consent"); setGeoConsent({ accepted: true }); setConsentOpen(false); pingPos(false); startWatch(); }
    catch { toast.error("Errore nel salvataggio del consenso"); }
  };
  const loadAgents = async () => { try { const r = await api.get("/geo/agents"); setAgentPositions(r.data.agents || []); } catch { /* ignore */ } };
  const loadTrail = async (id, day) => { if (!id) { setTrail([]); return; } try { const r = await api.get(`/geo/agent/${id}/trail`, { params: { day } }); setTrail(r.data.points || []); } catch { setTrail([]); } };
  useEffect(() => { if (trailAgent) loadTrail(trailAgent, trailDay); else setTrail([]); }, [trailAgent, trailDay]);
  const toggleAgents = () => {
    setShowAgents((v) => {
      const nv = !v;
      if (nv) { loadAgents(); if (!agentsPoll.current) agentsPoll.current = setInterval(loadAgents, 30000); }
      else if (agentsPoll.current) { clearInterval(agentsPoll.current); agentsPoll.current = null; }
      return nv;
    });
  };
  useEffect(() => () => { if (geoWatch.current) clearInterval(geoWatch.current); if (agentsPoll.current) clearInterval(agentsPoll.current); }, []);
  const allMapPoints = useMemo(() => {
    const extra = [];
    if (myPos) extra.push({ id: "__me", lat: myPos.lat, lng: myPos.lng, color: "#2563eb", radius: 9, borderColor: "#fff", borderWeight: 3, tooltip: "La mia posizione", label: "La mia posizione" });
    if (isAdmin && showAgents) agentPositions.forEach((a) => extra.push({ id: `__ag_${a.id}`, lat: a.lat, lng: a.lng, color: "#a855f7", radius: 8, borderColor: "#fff", borderWeight: 2, label: a.name, tooltip: `${a.name}${a.agency_name ? " · " + a.agency_name : ""} · agg. ${a.ts ? new Date(a.ts).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" }) : "—"}` }));
    if (isAdmin && trailAgent && trail.length) {
      const s = trail[0], e = trail[trail.length - 1];
      const hm = (t) => (t ? new Date(t).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" }) : "");
      extra.push({ id: "__trail_start", lat: s.lat, lng: s.lng, color: "#16a34a", radius: 7, borderColor: "#fff", borderWeight: 2, tooltip: `Inizio ${hm(s.ts)}` });
      if (trail.length > 1) extra.push({ id: "__trail_end", lat: e.lat, lng: e.lng, color: "#ef4444", radius: 7, borderColor: "#fff", borderWeight: 2, tooltip: `Ultimo ${hm(e.ts)}` });
    }
    return [...mapPoints, ...extra];
  }, [mapPoints, myPos, isAdmin, showAgents, agentPositions, trailAgent, trail]);
  const trailPath = useMemo(() => (isAdmin && trailAgent && trail.length > 1) ? [{ points: trail.map((t) => [t.lat, t.lng]), color: "#a855f7" }] : [], [isAdmin, trailAgent, trail]);

  const stats = useMemo(() => {
    const total = partners.length;
    const signed = partners.filter((p) => p.is_partner).length;
    const direzionali = partners.filter((p) => !p.agency_id).length;
    return { total, signed, clienti: total - signed, direzionali, tasso: total ? Math.round((signed / total) * 100) : 0 };
  }, [partners]);

  const openNew = () => { setEditing(null); setForm(EMPTY); setDialog(true); };
  const openEdit = (p) => {
    setEditing(p);
    setForm({ ...EMPTY, ...p, agency_id: p.agency_id || "none",
      partner_class: p.partner_class || "none",
      potenziale_annuo: p.potenziale_annuo ?? "", acquisti_torri_anno: p.acquisti_torri_anno ?? "",
      ultimo_contatto: p.ultimo_contatto || "", prossima_azione: p.prossima_azione || "",
      data_prossima_azione: p.data_prossima_azione || "", note_commerciali: p.note_commerciali || "",
      ultima_visita: p.ultima_visita || "", ultimo_acquisto: p.ultimo_acquisto || "", profilo_b2b: p.profilo_b2b || {} });
    setDialog(true);
  };

  const save = async () => {
    setSaving(true);
    const payload = {
      ...form,
      profilo_b2b: {
        ...(form.profilo_b2b || {}),
        dipendenti: _numOrNull(form.profilo_b2b?.dipendenti),
        fatturato_azienda: _numOrNull(form.profilo_b2b?.fatturato_azienda),
        capitale_sociale: _numOrNull(form.profilo_b2b?.capitale_sociale),
      },
      agency_id: form.agency_id === "none" ? null : form.agency_id,
      partner_class: form.partner_class === "none" ? null : form.partner_class,
      potenziale_annuo: form.potenziale_annuo === "" ? null : parseFloat(form.potenziale_annuo),
      acquisti_torri_anno: form.acquisti_torri_anno === "" ? null : parseFloat(form.acquisti_torri_anno),
      ultimo_contatto: form.ultimo_contatto || null,
      data_prossima_azione: form.data_prossima_azione || null,
      importo_totale: parseFloat(form.importo_totale) || 0,
      lat: (form.lat === "" || form.lat == null) ? null : parseFloat(form.lat),
      lng: (form.lng === "" || form.lng == null) ? null : parseFloat(form.lng),
      geo_manual: !!form.geo_manual,
      ultima_visita: form.ultima_visita || null,
      ultimo_acquisto: form.ultimo_acquisto || null,
    };
    try {
      if (editing) await api.put(`/partners/${editing.id}`, payload);
      else await api.post("/partners", payload);
      toast.success(editing ? "Partner aggiornato" : "Partner creato");
      setDialog(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Eliminare "${p.name}"?`)) return;
    await api.delete(`/partners/${p.id}`);
    toast.success("Cliente eliminato");
    load();
  };

  const [selectedIds, setSelectedIds] = useState(new Set());
  const [convDlg, setConvDlg] = useState(null);
  const toggleSel = (id) => setSelectedIds((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const bulkSetPartner = async (val) => {
    const ids = [...selectedIds];
    if (!ids.length) return;
    try { const r = await api.post("/partners/bulk-set-partner", { ids, is_partner: val }); toast.success(r.data.message); setSelectedIds(new Set()); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const bulkClearOverride = async () => {
    const ids = [...selectedIds];
    if (!ids.length) return;
    try { const r = await api.post("/partners/bulk-set-partner", { ids, clear_override: true }); toast.success(r.data.message); setSelectedIds(new Set()); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const convertToPartner = (p) => setConvDlg(p);

  const METODO_LABEL = { manuale: "Manuale", referente: "Agente", territorio: "Territorio", direzionale: "Direzionale" };
  const assignAgency = async (p, value) => {
    const aid = value === "none" ? null : value;
    try {
      await api.post(`/partners/${p.id}/assign`, { agency_id: aid });
      setPartners((list) => list.map((x) => x.id === p.id
        ? { ...x, agency_id: aid, agency_name: aid ? (agencies.find((a) => a.id === aid)?.name || null) : null, assign_method: "manuale" }
        : x));
      toast.success(aid ? "Cliente assegnato all'agenzia (manuale)" : "Cliente messo direzionale");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Assegnazione non riuscita"); }
  };

  const [provBusy, setProvBusy] = useState(false);
  const startEnrichProvince = async () => {
    setProvBusy(true);
    try {
      await api.post("/partners/enrich-province");
      toast.success("Recupero provincia/territorio avviato in background.");
      const t = setInterval(async () => {
        try {
          const r = await api.get("/partners/enrich-province/status");
          if (!r.data.running) {
            clearInterval(t); setProvBusy(false);
            if (r.data.total > 0) toast.success(`Territorio recuperato: ${r.data.enriched} localizzati, ${r.data.assigned} assegnati ad agenzia`);
            load();
          }
        } catch { /* ignore */ }
      }, 4000);
    } catch (e) { setProvBusy(false); toast.error(formatApiError(e.response?.data?.detail) || "Avvio non riuscito"); }
  };

  const doImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/partners/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(data.message);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const exportCsv = () => {
    const cols = ["name", "piva", "region", "province", "city", "agency_name", "certification_level",
      "importo_totale", "vesta_punti",
      "ultima_visita", "ultimo_acquisto"];
    const head = cols.join(",");
    const rows = filtered.map((p) => cols.map((c) => `"${(p[c] ?? "").toString().replace(/"/g, '""')}"`).join(","));
    const blob = new Blob([[head, ...rows].join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "partner_torri.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));
  const setPB = (k) => (e) => { const v = e?.target ? e.target.value : e; setForm((f) => ({ ...f, profilo_b2b: { ...(f.profilo_b2b || {}), [k]: v } })); };
  const _numOrNull = (v) => (v === "" || v == null ? null : (parseFloat(v) || null));

  return (
    <div className="space-y-6" data-testid="partners-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-black tracking-tight">Clienti</h1>
          <p className="text-sm text-muted-foreground">{num(stats.total)} clienti · {num(stats.signed)} Partner · {num(stats.clienti)} non Partner · <span className="text-amber-500">{num(stats.direzionali)} senza agenzia</span></p>
        </div>
        <div className="flex flex-wrap gap-2">
          {canQuickAdd && (
            <div className="flex flex-wrap items-center gap-2 rounded-lg border border-[#FDD003]/60 bg-[#FDD003]/[0.04] p-1.5" data-testid="clienti-group-crea">
              <QuickAdd onChanged={load} show={["activity"]} />
              <Button variant="outline" size="sm" onClick={openVisita} data-testid="clienti-registra-visita-btn"><ClipboardCheck className="mr-2 h-4 w-4" /> Registra visita</Button>
              <QuickAdd onChanged={load} show={["prospect"]} regions={regions} variant="default" />
            </div>
          )}
          {canEditCard && (
            <>
              <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls" className="hidden" onChange={doImport} data-testid="import-input" />
              <Button variant="outline" onClick={() => fileRef.current?.click()} data-testid="import-btn">
                <Upload className="mr-2 h-4 w-4" /> Importa CSV/Excel
              </Button>
              <Button variant="outline" onClick={exportCsv} data-testid="export-btn">
                <Download className="mr-2 h-4 w-4" /> Esporta
              </Button>
              <Button onClick={openNew} data-testid="add-partner-btn">
                <Plus className="mr-2 h-4 w-4" /> Nuovo Cliente
              </Button>
            </>
          )}
        </div>
      </div>

      <VisitaB2BDialog open={visitaOpen} onOpenChange={setVisitaOpen} partners={partners} prospects={prospectsLite} managers={managersLite} actor={insertActor} onSaved={load} />

      <Card className="p-3" data-testid="clienti-filter-bar">
        <div className="flex flex-wrap items-center gap-2">
          <Filter className="h-4 w-4 text-muted-foreground" />
          <Select value={regionFilter} onValueChange={(v) => { setRegionFilter(v); setProvinceFilter("all"); }}>
            <SelectTrigger className="h-9 w-[180px]" data-testid="clienti-region-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutte le regioni</SelectItem>
              {regions.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={provinceFilter} onValueChange={setProvinceFilter}>
            <SelectTrigger className="h-9 w-[180px]" data-testid="clienti-province-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutte le province</SelectItem>
              {provinceOptions.map((pr) => <SelectItem key={pr} value={pr}>{pr}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={agencyFilter} onValueChange={setAgencyFilter}>
            <SelectTrigger className="h-9 w-[220px]" data-testid="clienti-agency-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutte le agenzie</SelectItem>
              <SelectItem value="none">Senza agenzia (direzionali)</SelectItem>
              {agencies.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
            </SelectContent>
          </Select>
          {(regionFilter !== "all" || provinceFilter !== "all" || agencyFilter !== "all") && (
            <Button variant="outline" size="sm" onClick={() => { setRegionFilter("all"); setProvinceFilter("all"); setAgencyFilter("all"); }} data-testid="clienti-clear-filters">Azzera filtri ✕</Button>
          )}
        </div>
      </Card>

      {mode !== "table" && (
      <Card className="p-4" data-testid="clienti-map-card">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <h2 className="font-heading text-lg font-bold tracking-tight">Geolocalizzazione clienti</h2>
          <div className="flex flex-wrap items-center gap-2">
            {geoStat && <span className="text-xs text-muted-foreground" data-testid="clienti-geo-count">{num(geoStat.geocoded)} / {num(geoStat.total)} geolocalizzati{geoStat.running ? " · in corso…" : ""}</span>}
            {canLocate && <Button variant="outline" size="sm" onClick={startLocating} disabled={locating} data-testid="geo-locate-me-btn">{locating ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <LocateFixed className="mr-2 h-4 w-4" />} La mia posizione</Button>}
            {isAdmin && <Button variant={showAgents ? "default" : "outline"} size="sm" onClick={toggleAgents} data-testid="geo-show-agents-btn"><Users className="mr-2 h-4 w-4" /> {showAgents ? "Nascondi agenti" : "Mostra agenti"}</Button>}
            {geoStat && isAdmin && <Button variant="outline" size="sm" onClick={startEnrichProvince} disabled={provBusy} data-testid="clienti-enrich-province-btn">{provBusy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <MapPin className="mr-2 h-4 w-4" />} Assegna per territorio (da CAP)</Button>}
            {geoStat && <Button variant="outline" size="sm" onClick={startGeocode} disabled={geoBusy || geoStat.running} data-testid="clienti-geocode-btn">{(geoBusy || geoStat.running) ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <MapPin className="mr-2 h-4 w-4" />} Geolocalizza indirizzi</Button>}
          </div>
        </div>
        {isAdmin && showAgents && agentPositions.length > 0 && (
          <div className="mb-3 flex flex-wrap items-center gap-2 text-xs" data-testid="geo-trail-controls">
            <span className="text-muted-foreground">Percorso agente:</span>
            <Select value={trailAgent || "none"} onValueChange={(v) => setTrailAgent(v === "none" ? null : v)}>
              <SelectTrigger className="h-8 w-56" data-testid="geo-trail-agent"><SelectValue placeholder="Seleziona agente" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">— Nessuno —</SelectItem>
                {agentPositions.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <input type="date" value={trailDay} onChange={(e) => setTrailDay(e.target.value)} className="h-8 rounded-md border border-border bg-background px-2" data-testid="geo-trail-day" />
            {trailAgent && (<span className="flex items-center gap-3 text-muted-foreground" data-testid="geo-trail-count"><span>{trail.length} punti</span><span className="flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-full bg-[#16a34a]" /> Inizio</span><span className="flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-full bg-[#ef4444]" /> Ultimo</span></span>)}
          </div>
        )}
        {geoStat && geoStat.total > 0 && (
          <div className="mb-3 h-2 w-full overflow-hidden rounded-full bg-muted"><div className="h-full rounded-full bg-primary transition-all" style={{ width: `${geoStat.total ? Math.round((geoStat.geocoded / geoStat.total) * 100) : 0}%` }} /></div>
        )}
        <PointsMap testid="clienti-map" points={allMapPoints} paths={trailPath} onPointClick={(id) => { const s = String(id); if (s.startsWith("__ag_")) { setTrailAgent(s.replace("__ag_", "")); return; } if (s.startsWith("__")) return; navigate(`/partners/${id}`); }} legend={<>
          <span className="flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-full bg-[#16a34a]" /> Partner</span>
          <span className="flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-full bg-[#f59e0b]" /> Cliente</span>
          {myPos && <span className="flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-full bg-[#2563eb]" /> La mia posizione</span>}
          {isAdmin && showAgents && <span className="flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-full bg-[#a855f7]" /> Agenti</span>}
        </>} />
      </Card>
      )}

      <Dialog open={consentOpen} onOpenChange={setConsentOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="geo-consent-dialog">
          <DialogHeader><DialogTitle>Geolocalizzazione</DialogTitle></DialogHeader>
          <div className="space-y-3 text-sm text-muted-foreground">
            <p>Attivando <b className="text-foreground">«La mia posizione»</b> acconsenti a condividere la tua posizione GPS con l'azienda (visibile solo alla Direzione) per la mappatura della rete commerciale.</p>
            <ul className="list-disc space-y-1 pl-5">
              <li>La posizione si aggiorna solo quando l'app è aperta, nelle <b>ore lavorative</b> (Lun–Ven, 8:00–19:00).</li>
              <li>Serve all'agenzia per <b>localizzarsi sulla mappa</b> rispetto a clienti e prospect vicini.</li>
              <li>Puoi interrompere in qualsiasi momento <b>chiudendo l'app</b>.</li>
            </ul>
            <p className="text-[12px]">Trattamento ai sensi del GDPR: finalità di organizzazione dell'attività commerciale. Il consenso è revocabile.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConsentOpen(false)} data-testid="geo-consent-decline">Annulla</Button>
            <Button onClick={acceptConsent} data-testid="geo-consent-accept">Acconsento e attivo</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {mode !== "map" && (
      <Card className="p-4">
        <div className="mb-4 grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6" data-testid="portafoglio-kpi">
          {(() => {
            const cnt = { A: 0, B: 0, C: 0, D: 0 };
            filtered.forEach((p) => { const k = (p.partner_class || "").toUpperCase(); if (cnt[k] != null) cnt[k]++; });
            const nPartner = filtered.filter((p) => p.is_partner).length;
            const tiles = [
              ["Clienti", filtered.length, "text-foreground"],
              ["Partner", nPartner, "text-green-500"],
              ["Classe A", cnt.A, "text-emerald-500"],
              ["Classe B", cnt.B, "text-primary"],
              ["Classe C", cnt.C, "text-amber-500"],
              ["Classe D", cnt.D, "text-muted-foreground"],
            ];
            return tiles.map(([label, val, cls]) => (
              <div key={label} className="rounded-lg border border-border bg-muted/30 p-3" data-testid={`portafoglio-kpi-${label}`}>
                <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div>
                <div className={`mt-1 font-heading text-2xl font-black ${cls}`}>{val}</div>
              </div>
            ));
          })()}
        </div>
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input className="w-64 pl-9" placeholder="Cerca per nome, regione, città…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="partner-search" />
            </div>
            <div className="inline-flex rounded-md border border-white/10 p-0.5" data-testid="partner-presidio-tabs">
              {[["attivo", "Attivi"], ["panchina", "Panchina"], ["cimitero", "Cimitero"]].map(([v, l]) => (
                <button key={v} onClick={() => setPresidioView(v)} data-testid={`partner-presidio-tab-${v}`}
                  className={`rounded px-3 py-1 text-xs font-semibold transition-colors ${presidioView === v ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>{l}{v !== "attivo" ? ` (${presidioCounts[v] ?? 0})` : ""}</button>
              ))}
            </div>
            <Button
              variant={pfilter === "partner" ? "default" : "outline"}
              size="sm"
              onClick={() => setPfilter(pfilter === "partner" ? "all" : "partner")}
              data-testid="only-partner-btn"
              className={pfilter === "partner"
                ? "bg-green-600 font-semibold text-white hover:bg-green-600/90"
                : "border-green-600/60 font-semibold text-green-700 hover:bg-green-600/10 dark:text-green-400"}>
              <Handshake className="mr-2 h-4 w-4" /> Solo Partner
            </Button>
            <Select value={pfilter} onValueChange={setPfilter}>
              <SelectTrigger className="w-[210px]" data-testid="client-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti i clienti</SelectItem>
                <SelectItem value="partner">Solo Partner</SelectItem>
                <SelectItem value="cliente">Solo Clienti (non Partner)</SelectItem>
                <SelectItem value="sciolti">Senza agenzia (da assegnare)</SelectItem>
              </SelectContent>
            </Select>
            <Select value={profFilter} onValueChange={setProfFilter}>
              <SelectTrigger className="w-[190px]" data-testid="prof-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Profilazione: tutte</SelectItem>
                <SelectItem value="quad_chiave">Profilo: Clienti chiave</SelectItem>
                <SelectItem value="quad_sviluppare">Profilo: Da sviluppare</SelectItem>
                <SelectItem value="quad_mantenere">Profilo: Da mantenere</SelectItem>
                <SelectItem value="quad_marginali">Profilo: Marginali</SelectItem>
                <SelectItem value="A">Categoria A</SelectItem>
                <SelectItem value="B">Categoria B</SelectItem>
                <SelectItem value="C">Categoria C</SelectItem>
                <SelectItem value="opportunita">Opportunità ★</SelectItem>
                <SelectItem value="a_rischio">A rischio</SelectItem>
                <SelectItem value="incompleta">Profilazione incompleta</SelectItem>
              </SelectContent>
            </Select>
            <Button variant={sortOpp ? "default" : "outline"} size="sm" onClick={() => setSortOpp((v) => !v)} data-testid="sort-opportunita">Ordina per opportunità</Button>
          </div>
          <div className="rounded-md border border-border px-3 py-1.5 text-right" data-testid="conversion-kpi">
            <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Tasso conversione a Partner</div>
            <div className="font-heading text-xl font-black text-primary">{stats.tasso}%</div>
          </div>
        </div>

        <ClassLegend className="mb-3 rounded-md border border-border bg-muted/30 px-3 py-2" />

        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
        ) : (
          <div className="overflow-x-auto">
            {(canConvert || canEditCard) && selectedIds.size > 0 && (
              <div className="mb-3 flex flex-wrap items-center gap-2 rounded-md border border-primary/40 bg-primary/5 p-2" data-testid="partner-bulk-bar">
                <span className="text-sm font-semibold">{selectedIds.size} selezionati</span>
                <Button size="sm" onClick={() => bulkSetPartner(true)} data-testid="bulk-mark-partner"><Handshake className="mr-1 h-3 w-3" /> Forza Partner</Button>
                <Button size="sm" variant="outline" onClick={() => bulkSetPartner(false)} data-testid="bulk-unmark-partner">Forza NON-partner</Button>
                <Button size="sm" variant="ghost" onClick={() => bulkClearOverride()} data-testid="bulk-clear-override">Ripristina auto (sync)</Button>
                <Button size="sm" variant="ghost" onClick={() => setSelectedIds(new Set())}>Deseleziona</Button>
              </div>
            )}
            <Table>
              <TableHeader>
                <TableRow>
                  {canConvert && <TableHead className="w-8"><Checkbox checked={filtered.length > 0 && filtered.every((p) => selectedIds.has(p.id))} onCheckedChange={(v) => setSelectedIds(v ? new Set(filtered.map((p) => p.id)) : new Set())} data-testid="select-all-partners" /></TableHead>}
                  <TableHead>Partner</TableHead>
                  <TableHead>Regione</TableHead>
                  <TableHead>Agenzia</TableHead>
                  <TableHead>Tipo</TableHead>
                  <TableHead>Classe</TableHead>
                  <TableHead className="text-right"><span className="inline-flex items-center justify-end gap-1 text-[#E3B341]"><VestaBadge /> punti Vesta <VestaInfo /></span></TableHead>
                  <TableHead>Programma consigliato</TableHead>
                  <TableHead>Presidio</TableHead>
                  {(canEditCard || canPresidio) && <TableHead className="text-right">Azioni</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((p) => (
                  <TableRow key={p.id} data-testid={`partner-row-${p.id}`}>
                    {canConvert && <TableCell><Checkbox checked={selectedIds.has(p.id)} onCheckedChange={() => toggleSel(p.id)} data-testid={`select-partner-${p.id}`} /></TableCell>}
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {p.has_logo && <img src={partnerLogoUrl(p.id)} alt="" className="h-7 w-7 shrink-0 rounded border border-border bg-white object-contain p-0.5" loading="lazy" data-testid={`partner-logo-thumb-${p.id}`} />}
                        <div>
                          <div className="flex items-center gap-1.5">
                            <Link to={`/partners/${p.id}`} className="font-semibold hover:text-primary hover:underline" data-testid={`partner-link-${p.id}`}>{p.name}</Link>
                            <Link to={`/configuratore/${p.id}`} title="Apri nel Configuratore" data-testid={`configuratore-partner-${p.id}`} className="text-muted-foreground transition-colors hover:text-primary">
                              <Wrench className="h-3.5 w-3.5" />
                            </Link>
                          </div>
                          <div className="text-xs text-muted-foreground">{p.city} · {p.certification_level}</div>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>{p.region || "—"}</TableCell>
                    <TableCell>
                      {canEditCard ? (
                        <div className="flex flex-col gap-0.5">
                          <Select value={p.agency_id || "none"} onValueChange={(v) => assignAgency(p, v)}>
                            <SelectTrigger className="h-8 w-[190px] text-xs" data-testid={`assign-agency-${p.id}`}><SelectValue placeholder="Direzionale" /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">— Direzionale (nessuna)</SelectItem>
                              {agencies.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
                            </SelectContent>
                          </Select>
                          {p.assign_method && <span className="text-[10px] text-muted-foreground">{METODO_LABEL[p.assign_method] || p.assign_method}</span>}
                        </div>
                      ) : <span className="text-sm text-muted-foreground">{p.agency_name || "—"}</span>}
                    </TableCell>
                    <TableCell>
                      {p.is_partner
                        ? <Badge className="border border-green-300 bg-green-100 text-[11px] text-green-800">Partner</Badge>
                        : (
                          <div className="flex items-center gap-1">
                            <Badge variant="outline" className="text-[11px] text-muted-foreground">Cliente</Badge>
                            {canConvert && <Button variant="ghost" size="sm" className="h-6 px-1.5 text-[11px] text-green-700" onClick={() => convertToPartner(p)} data-testid={`convert-partner-${p.id}`}><Handshake className="mr-1 h-3 w-3" />Converti in partner</Button>}
                          </div>
                        )}
                      {p.presidio_status && p.presidio_status !== "attivo" && (
                        <div className="mt-1 text-[10px] text-muted-foreground" data-testid={`partner-presidio-reason-${p.id}`}>{p.presidio_status === "cimitero" ? "Cimitero" : "Panchina"}: {p.presidio_reason}</div>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge className={`text-[11px] ${CAT_STYLE[p.partner_class || p.profilazione?.categoria_auto] || "bg-muted"}`} data-testid={`partner-classe-${p.id}`}>{p.partner_class || p.profilazione?.categoria_auto || "N/D"}</Badge>
                    </TableCell>
                    <TableCell className="text-right font-mono text-sm font-semibold text-[#E3B341]" data-testid={`partner-vesta-${p.id}`}>{p.is_partner && p.vesta_punti != null ? p.vesta_punti.toLocaleString("it-IT", { maximumFractionDigits: 1 }) : "—"}</TableCell>
                    <TableCell>
                      {p.profilazione?.programma_consigliato ? (
                        <div className="flex flex-col" data-testid={`partner-programma-${p.id}`}>
                          <span className="text-sm font-semibold">{p.profilazione.programma_consigliato.label}</span>
                          <span className="text-[11px] text-muted-foreground">{p.profilazione.programma_consigliato.visite_mese} visite/mese · {p.profilazione.programma_consigliato.azioni_mese ?? ((Number(p.profilazione.programma_consigliato.visite_mese) || 0) + (Number(p.profilazione.programma_consigliato.contatti_mese) || 0))} azioni tot/mese · priorità {p.profilazione.programma_consigliato.priorita}</span>
                        </div>
                      ) : <span className="text-xs text-muted-foreground">—</span>}
                    </TableCell>
                    <TableCell data-testid={`partner-presidio-linea-${p.id}`}>
                      {p.presidio_linea ? (
                        <Badge className={`text-[11px] border ${p.presidio_linea.status === "in_linea" ? "bg-emerald-100 text-emerald-800 border-emerald-300" : p.presidio_linea.status === "in_scadenza" ? "bg-amber-100 text-amber-800 border-amber-300" : "bg-red-100 text-red-700 border-red-300"}`}>
                          {p.presidio_linea.status === "in_linea" ? "In linea" : p.presidio_linea.status === "in_scadenza" ? "In scadenza" : "Scaduto"}
                        </Badge>
                      ) : <span className="text-xs text-muted-foreground">—</span>}
                    </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          {canPresidio && (presidioView === "attivo" ? (
                            <Button variant="ghost" size="icon" title="Panchina / Cimitero" onClick={() => setPresidioDlg({ open: true, target: { id: p.id, name: p.name }, mode: "move" })} data-testid={`partner-bench-${p.id}`}><Skull className="h-4 w-4 text-amber-500" /></Button>
                          ) : (
                            <Button variant="ghost" size="sm" className="h-7 px-2 text-[10px] text-primary" onClick={() => setPresidioDlg({ open: true, target: { id: p.id, name: p.name }, mode: "restore" })} data-testid={`partner-restore-presidio-${p.id}`}>Riporta attivo</Button>
                          ))}
                          {canEditPartner && <Button variant="ghost" size="icon" onClick={() => openEdit(p)} data-testid={`edit-${p.id}`}><Pencil className="h-4 w-4" /></Button>}
                          {canEditCard && <Button variant="ghost" size="icon" onClick={() => remove(p)} data-testid={`delete-${p.id}`}><Trash2 className="h-4 w-4 text-destructive" /></Button>}
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
                {filtered.length === 0 && (
                  <TableRow><TableCell colSpan={(canEditCard || canPresidio) ? 14 : 13} className="py-12 text-center text-muted-foreground">Nessun cliente trovato.</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>
      )}

      <PresidioDialog open={presidioDlg.open} onOpenChange={(o) => setPresidioDlg((s) => ({ ...s, open: o }))} kind="partners" target={presidioDlg.target} mode={presidioDlg.mode} onDone={load} />

      <Dialog open={dialog} onOpenChange={(o) => { setDialog(o); if (!o) setCsCandidates(null); }}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl" data-testid="partner-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Modifica Partner" : "Nuovo Partner"}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Ragione Sociale" className="sm:col-span-2"><Input value={form.name} onChange={set("name")} data-testid="f-name" /></Field>
            <Field label="Partita IVA"><Input value={form.piva} onChange={set("piva")} /></Field>
            {editing && (
              <div className="sm:col-span-2 rounded-md border border-primary/20 bg-primary/5 p-3" data-testid="creditsafe-block">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="text-xs text-muted-foreground">Arricchisci anagrafica e dati aziendali da Creditsafe (P.IVA, provincia, stato attività, ATECO).</div>
                  <Button type="button" variant="outline" size="sm" onClick={() => enrichCreditsafe()} disabled={csBusy} data-testid="creditsafe-enrich-btn">
                    {csBusy ? <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" /> : <Database className="mr-2 h-3.5 w-3.5" />} Arricchisci da Creditsafe
                  </Button>
                </div>
                {csCandidates && (
                  <div className="mt-2 space-y-1">
                    <div className="text-[11px] font-semibold text-primary">Seleziona l'azienda corretta:</div>
                    {csCandidates.map((c) => (
                      <button key={c.connect_id} type="button" onClick={() => enrichCreditsafe(c.connect_id)} className="block w-full rounded border border-white/10 bg-black/30 px-2 py-1.5 text-left text-xs hover:border-primary/40" data-testid={`cs-cand-${c.connect_id}`}>
                        <span className="font-semibold">{c.name}</span> · P.IVA {c.vat_no || "—"} · {c.province || "—"} · <span className={c.status === "active" ? "text-green-400" : "text-red-400"}>{c.status || "—"}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
            <div className="sm:col-span-2 mt-1 border-t border-border pt-3 font-mono text-[10px] uppercase tracking-widest text-primary">Dati aziendali (back office / Creditsafe)</div>
            <Field label="N. dipendenti"><Input type="number" value={form.profilo_b2b?.dipendenti ?? ""} onChange={setPB("dipendenti")} data-testid="f-dipendenti" /></Field>
            <Field label="Vendite aziendali (€)"><Input type="number" value={form.profilo_b2b?.fatturato_azienda ?? ""} onChange={setPB("fatturato_azienda")} data-testid="f-fatturato-azienda" /></Field>
            <Field label="Capitale sociale (€)" className="sm:col-span-2"><Input type="number" value={form.profilo_b2b?.capitale_sociale ?? ""} onChange={setPB("capitale_sociale")} data-testid="f-capitale-sociale" /></Field>
            <Field label="Referente"><Input value={form.referente} onChange={set("referente")} data-testid="f-referente" /></Field>
            <Field label="Email"><Input value={form.email} onChange={set("email")} /></Field>
            <Field label="Telefono"><Input value={form.phone} onChange={set("phone")} /></Field>
            <Field label="Regione">
              <Select value={form.region || undefined} onValueChange={set("region")}>
                <SelectTrigger data-testid="f-region"><SelectValue placeholder="Seleziona" /></SelectTrigger>
                <SelectContent>{regions.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Provincia"><Input value={form.province} onChange={set("province")} /></Field>
            <Field label="Città"><Input value={form.city} onChange={set("city")} /></Field>
            <Field label="Indirizzo"><Input value={form.address} onChange={set("address")} /></Field>
            <div className="sm:col-span-2 mt-1 border-t border-border pt-3 font-mono text-[10px] uppercase tracking-widest text-primary">Sede operativa e posizione sulla mappa</div>
            <Field label="Indirizzo operativo (se diverso dalla sede legale)"><Input value={form.sede_op_address || ""} onChange={set("sede_op_address")} data-testid="f-sede-op-address" /></Field>
            <Field label="Città operativa"><Input value={form.sede_op_city || ""} onChange={set("sede_op_city")} data-testid="f-sede-op-city" /></Field>
            <Field label="CAP operativo"><Input value={form.sede_op_cap || ""} onChange={set("sede_op_cap")} data-testid="f-sede-op-cap" /></Field>
            <div className="sm:col-span-2 space-y-2">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="text-xs text-muted-foreground" data-testid="f-geo-status">
                  {form.geo_manual ? "📍 Posizione fissata manualmente" : (form.lat != null ? "Posizione da indirizzo" : "Nessuna posizione")}
                  {form.lat != null ? ` · ${(+form.lat).toFixed(5)}, ${(+form.lng).toFixed(5)}` : ""}
                </span>
                {editing && <Button type="button" variant="outline" size="sm" onClick={ricalcolaPos} disabled={geoOne} data-testid="f-ricalcola-pos">{geoOne ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <MapPin className="mr-1.5 h-4 w-4" />} Ricalcola da indirizzo</Button>}
              </div>
              <PinPicker lat={form.lat != null ? +form.lat : null} lng={form.lng != null ? +form.lng : null} onSet={(la, ln) => setForm((f) => ({ ...f, lat: la, lng: ln, geo_manual: true }))} />
              <p className="text-[11px] text-muted-foreground">Trascina o clicca il segnaposto per fissare la posizione esatta (override manuale). «Ricalcola da indirizzo» ricava il pin dalla sede operativa (o, se vuota, dalla sede legale).</p>
            </div>
            <Field label="Certificazione">
              <Select value={form.certification_level} onValueChange={set("certification_level")}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{CERTS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            {!isAgency && (
            <Field label="Partnership firmata">
              <Select value={form.is_partner ? "yes" : "no"} onValueChange={(v) => setForm((f) => ({ ...f, is_partner: v === "yes" }))}>
                <SelectTrigger data-testid="f-ispartner"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="yes">Partner (firmata)</SelectItem>
                  <SelectItem value="no">Cliente (non firmata)</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            )}
            {!isAgency && (
            <Field label="Agenzia di riferimento">
              <Select value={form.agency_id} onValueChange={set("agency_id")}>
                <SelectTrigger data-testid="f-agency"><SelectValue placeholder="Nessuna" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Nessuna</SelectItem>
                  {agencies.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            )}
            <Field label="Importo Totale (€)"><Input type="number" value={form.importo_totale} onChange={set("importo_totale")} /></Field>
            <Field label="Ultima Visita"><Input value={form.ultima_visita} onChange={set("ultima_visita")} placeholder="GG/MM/AAAA" /></Field>
            <Field label="Ultimo Acquisto"><Input value={form.ultimo_acquisto} onChange={set("ultimo_acquisto")} placeholder="AAAA-MM-GG" /></Field>
            <div className="sm:col-span-2 mt-2 border-t border-border pt-3 font-mono text-[10px] uppercase tracking-widest text-primary">Profilazione Commerciale</div>
            <Field label="Classe Partner">
              <Select value={form.partner_class} onValueChange={set("partner_class")}>
                <SelectTrigger data-testid="f-class"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">—</SelectItem>
                  <SelectItem value="A">A</SelectItem>
                  <SelectItem value="B">B</SelectItem>
                  <SelectItem value="C">C</SelectItem>
                  <SelectItem value="D">D</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="Potenziale acquisti annuo (€)"><Input type="number" value={form.potenziale_annuo} onChange={set("potenziale_annuo")} data-testid="f-potenziale" /></Field>
            <Field label="Acquisti TORRI anno (€)"><Input type="number" value={form.acquisti_torri_anno} onChange={set("acquisti_torri_anno")} data-testid="f-acquisti" /></Field>
            <Field label="Ultimo contatto commerciale"><Input value={form.ultimo_contatto} onChange={set("ultimo_contatto")} placeholder="GG/MM/AAAA" /></Field>
            <Field label="Prossima azione"><Input value={form.prossima_azione} onChange={set("prossima_azione")} /></Field>
            <Field label="Data prossima azione"><Input type="date" value={form.data_prossima_azione} onChange={set("data_prossima_azione")} /></Field>
            <Field label="Note commerciali" className="sm:col-span-2"><Input value={form.note_commerciali} onChange={set("note_commerciali")} /></Field>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(false)}>Annulla</Button>
            <Button onClick={save} disabled={saving || !form.name} data-testid="save-partner-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Salva"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConvertPartnerDialog partner={convDlg} onOpenChange={(o) => { if (!o) setConvDlg(null); }} onDone={load} />
    </div>
  );
}

const Field = ({ label, children, className = "" }) => (
  <div className={`space-y-1.5 ${className}`}>
    <Label className="text-xs">{label}</Label>
    {children}
  </div>
);
